/**
 * Shared signup pages (Foodbuy, HSSI) — field validation
 *
 * Declarative: any input with data-signup-validate="<rule>" is sanitized while
 * typing/pasting and validated through the native constraint API
 * (setCustomValidity), so the browser blocks submit and shows the message.
 * Rules: name, phone, city, zip, email.
 * ZIP fields may carry data-signup-country="<select id>" to pick the format.
 *
 * Fields filled programmatically (Google Places, sector lookup, "same as"
 * copy) are re-validated on the submit button click, before native validation.
 * Rendered markup: snippets/signup-personal-info.liquid,
 * snippets/signup-ap-contact.liquid, snippets/signup-shipping-address.liquid
 * and the City/ZIP inputs in the Foodbuy/HSSI sections.
 */

"use strict";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SIGNUP_VALIDATE_SELECTOR = "[data-signup-validate]";
const SIGNUP_SUBMIT_SELECTOR = ".company_custom_button";
const SIGNUP_FORM_ID = "registration-form";
const SIGNUP_SUBMITTED_CLASS = "signup-submitted";
const SIGNUP_COUNTRY_US = "United States";
const SIGNUP_COUNTRY_CA = "Canada";

const SIGNUP_RULES = {
  name: {
    strip: /[^\p{L}\s]/gu,
    valid: /^\p{L}+(?:\s+\p{L}+)*$/u,
    message: "Please use letters only — no numbers or special characters.",
  },
  phone: {
    strip: /[^\d+]/g,
    valid: /^\+?\d{10,15}$/,
    message: "Please enter a valid phone number (digits only, optional leading +, 10–15 digits).",
  },
  city: {
    strip: /[^\p{L}\s.'-]/gu,
    valid: /^\p{L}[\p{L}\s.'-]*$/u,
    message: "Please enter a valid city — numbers aren't allowed.",
  },
  zip: {
    strip: /[^A-Za-z0-9 -]/g,
    message: "Please enter a valid postal code.",
  },
  email: {
    strip: null,
    valid: /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/,
    message: "Please enter a valid email address (e.g. name@company.com).",
  },
};

const SIGNUP_ZIP_FORMATS = {
  [SIGNUP_COUNTRY_US]: {
    valid: /^\d{5}(?:-\d{4})?$/,
    message: "Please enter a valid ZIP code (12345 or 12345-6789).",
  },
  [SIGNUP_COUNTRY_CA]: {
    valid: /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/,
    message: "Please enter a valid postal code (A1A 1A1).",
  },
};
const SIGNUP_ZIP_DEFAULT_FORMAT = {
  valid: /^[A-Za-z0-9][A-Za-z0-9 -]{1,8}[A-Za-z0-9]$/,
  message: SIGNUP_RULES.zip.message,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Applies a rule's character filter to a raw value.
 * @param {string} ruleName key of SIGNUP_RULES
 * @param {string} value raw input value
 * @returns {string} sanitized value
 */
function signupSanitizeValue(ruleName, value) {
  const rule = SIGNUP_RULES[ruleName];
  let result = value;
  if (rule && rule.strip) {
    result = result.replace(rule.strip, "");
  }
  if (ruleName === "phone") {
    result = result.replace(/(?!^)\+/g, "");
  }
  return result;
}

/**
 * Resolves the {valid, message} pair for a field (ZIP depends on country).
 * @param {HTMLInputElement} field
 * @returns {{valid: RegExp, message: string}}
 */
function signupResolveFormat(field) {
  const ruleName = field.dataset.signupValidate;
  let format = SIGNUP_RULES[ruleName];
  if (ruleName === "zip") {
    const countryEl = document.getElementById(field.dataset.signupCountry || "");
    const country = countryEl ? countryEl.value : "";
    format = SIGNUP_ZIP_FORMATS[country] || SIGNUP_ZIP_DEFAULT_FORMAT;
  }
  return format;
}

/**
 * Removes disallowed characters from a field, keeping the caret in place.
 * @param {HTMLInputElement} field
 */
function signupSanitizeField(field) {
  const before = field.value;
  const after = signupSanitizeValue(field.dataset.signupValidate, before);
  if (after !== before) {
    const caret = field.selectionStart;
    field.value = after;
    if (typeof caret === "number") {
      const kept = signupSanitizeValue(field.dataset.signupValidate, before.slice(0, caret)).length;
      try {
        field.setSelectionRange(kept, kept);
      } catch (e) {
        // input types without selection support: ignore
      }
    }
  }
}

/**
 * Sets the field's custom validity from its rule.
 * @param {HTMLInputElement} field
 */
function signupValidateField(field) {
  const value = field.value.trim();
  const format = signupResolveFormat(field);
  let message = "";
  if (!field.disabled && format && field.value !== "") {
    message = format.valid.test(value) ? "" : format.message;
  }
  field.setCustomValidity(message);
}

/**
 * Sanitizes and validates a field.
 * @param {HTMLInputElement} field
 */
function signupCheckField(field) {
  signupSanitizeField(field);
  signupValidateField(field);
}

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------

/**
 * Flags the form as submitted so :invalid fields get their red border (see
 * company-signup-layout.css). Pure CSS afterwards: borders clear once valid.
 */
function signupMarkSubmitted() {
  const form = document.getElementById(SIGNUP_FORM_ID);
  if (form) {
    form.classList.add(SIGNUP_SUBMITTED_CLASS);
  }
}

/**
 * Wires listeners for every [data-signup-validate] field on the page.
 */
function signupInitValidation() {
  const fields = Array.from(document.querySelectorAll(SIGNUP_VALIDATE_SELECTOR));
  if (fields.length === 0) {
    return;
  }
  fields.forEach((field) => {
    field.addEventListener("input", () => signupCheckField(field));
    field.addEventListener("blur", () => {
      field.value = field.value.trim();
      signupCheckField(field);
    });
    const countryEl = document.getElementById(field.dataset.signupCountry || "");
    if (countryEl) {
      countryEl.addEventListener("change", () => signupValidateField(field));
    }
  });
  const submitButton = document.querySelector(SIGNUP_SUBMIT_SELECTOR);
  if (submitButton) {
    submitButton.addEventListener("click", () => {
      fields.forEach(signupCheckField);
      signupMarkSubmitted();
    }, true);
  }
}

document.addEventListener("DOMContentLoaded", signupInitValidation);
